/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import { INativeNotify, INativeNotifyAction, getNotificationController } from "../utils/native/notificationController";
import { getVersion } from "../utils/native/nativeContainer";
import { isAddinBottomBarSupported, setAddinBottomBar } from "../utils/native/addinBottomBar";
import {
    IBottomNavigationItem,
    IInstalledAddin,
    clearAddinNavigationItem,
    findUniquelyNamedAddin,
    isAddinNavigationItemSupported,
    setAddinNavigationItem
} from "../utils/native/addinNavigationItem";
import { nativeSpeak } from "../utils/native/nativeSpeak";
import { announceLoggedInDriversLeaving, getAddinTheme } from "../utils/native/addinLifecycleEvents";
import { isNativeApp } from "../utils/native/nativeHelpers";
import { checkOverlayPermission, requestOverlayPermission, displayOverlay } from "../utils/native/nativeOverlayApi";
import { CancelablePromise } from "../utils/cancelablePromise";
import { getCurrentVehicle } from "../utils/device";
import { TrailerAttachments } from "../model/TrailerAttachments";
import { getShipments } from "../model/Shipments";
import HosProcess from "../services/hosProcess";
import { BaseCache, makeDeviceCache, makeTrailerCache } from "../utils/storage/cache";
import {
    type IValidation,
    type IHosAvailability,
    type IViolation,
    getMinAvailability,
    getAvailabilityName,
    getIconForAvailability,
    convertMsToString,
    TranslatableDisplayStrings,
    type IHosRuleset
} from "hos-tools";
import minAvailabilityHTML from "./template/minAvailability.tmpl";
import Api from "../services/api";
import { Trailer, ITrailer } from "../model/Trailer";
import Service from "./base";
import Sync from "../sync/sync";
import { getServerDate } from "../utils/storage/date";
import constants, { DAYS, MINUTES } from "../constants";
import { TrailerAttachment } from "../model/TrailerAttachment";
import { Shipment, IShipment } from "../model/Shipment";
import { extend, hashToArray, jsonClone } from "../utils/vanilla";
import { Router } from "../router";
import { DriveStore } from "../store/reducers";
import { observeStore } from "../utils/store";
import { getLastKnownDriving, readDriving } from "../utils/native/addinLifecycleEvents";
import Message, { IMessage } from "../model/Message";
import DutyStatusLog, { NODEVICEID } from "../model/DutyStatusLog";
import DVIRLog, { IDVIRLog } from "../model/DVIRLog";
import { isUnsyncedDvirLog } from "../store/dvirLogs/reducers";
import { TApiErrorCallback, TApiSuccessCallback, IApiCall, TApiCredentialsCallback, TMultiCallParam } from "../utils/api.interfaces";
import { ICredentials, ICredentialsWithServer } from "../utils/credentialsStore";
import { UserService } from "../utils/users";
import type { IMobileStore } from "../store/mobile/types";
import TranslationService from "./translation";
import PictureOptionDialog from "../ui/framework/pictureOptionDialog";
import { IImageResult } from "../utils/native/nativeFileApi";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { handleApi } from "../utils/error";
import { Device } from "../model/Device";
import * as logger from "../utils/logger";
import { IamSessionIdManager } from "./IamSessionIdManager";
import { Unsubscribe } from "../lib/redux";
import _ from "underscore";
import { getTranslatedString } from "../utils/helpers";
import { getToken, isGetTokenModule } from "native-sdk";

const SYNC_TIMEOUT = 20000;
const IAM_SESSIONID_REFRESH_INTERVAL_MINUTES = 25 * MINUTES;

/**
 * How far back api.mobile.dvir.get() returns synced DVIR logs. Matches DriveNative's
 * DVIR_LOOKBACK_DAYS, so the documented window does not change when DVIR moves to RN.
 */
export const ADDIN_DVIR_LOOKBACK_DAYS = 14;

/**
 * An entry returned by api.mobile.dvir.get(): the DVIRLog entity with Drive's client-only
 * sync fields removed, plus whether the log still exists only on this device.
 */
export type TAddinDvirLog = IDVIRLog & {
    isPendingSync: boolean;
};

const DVIR_CLIENT_ONLY_FIELDS = ["modified", "previousState", "fromServer"];

/**
 * Drops Drive's client-side fields (sync markers and "_"-prefixed values) from an object in place.
 * Same rule as BaseSync.removeClientSideProperties (app/sync/base.ts), which AddinService can't
 * inherit; keep the two field lists in sync.
 */
function stripClientOnlyFields(entity: object) {
    const record = entity as Record<string, unknown>;
    Object.keys(record).forEach(key => {
        if (key.charAt(0) === "_" || DVIR_CLIENT_ONLY_FIELDS.indexOf(key) !== -1) {
            delete record[key];
        }
    });
}

/** Epoch ms of an ISO dateTime, or NaN when missing or unparseable. */
function getDvirTime(log: IDVIRLog): number {
    return log.dateTime ? Date.parse(log.dateTime) : NaN;
}

/** Result of api.mobile.theme.get(). */
export interface IAddinTheme {
    /** True when Drive renders add-ins in night mode (the `#app.nightMode` theme). */
    nightMode: boolean;
}

/** Result of api.mobile.driving.get(). */
export interface IAddinDriving {
    /** True or false as the app last knew it, held through GPS silence; null while the source that owns the driving state has not known it yet. */
    isDriving: boolean | null;
}

export type MobileLifecycleEvent =
    | "appBackground" | "appForeground" | "networkStateChange" | "hosStatusChange" | "themeChange"
    | "driverLogin" | "driverLogout" | "driverSeatChange" | "vehicleChange" | "drivingChange";

type MobileEventCallback = (payload: unknown) => void;
// setDevice is a function on the extended app state, not observable store data.
type MobileStateField = Exclude<keyof IMobileStore, "setDevice">;
type MobileStateSnapshot = ReturnType<DriveStore["getState"]>["mobile"];
// Record<MobileStateField, true> makes this exhaustive at compile time: adding a
// field to IMobileStore without listing it here (or listing a removed one) is a
// type error, so listenTo(fields, cb) can't silently reject a valid field.
const LISTENABLE_MOBILE_FIELDS: Record<MobileStateField, true> = {
    pageState: true, charging: true, batteryLevel: true, background: true, online: true,
    deviceCommunicating: true, lastDeviceCommunicationDateTime: true, gpsConnected: true,
    lastPosition: true, embed: true, eldMalfunction: true, eldDiagnostic: true,
    diagnosticViewed: true, newUpcomingViolation: true, driverActionNecessary: true, language: true,
    ioxAttached: true, gpsData: true, lastViewedLog: true, device: true, newMessage: true,
    previousPage: true, currentPage: true, loginRequired: true, pcLimitWarningMessage: true,
    pendoResourceCenterAvailable: true, tripCategorizationSyncInterval: true, lastKnownSpeed: true,
    iam: true, iamSessionInvalidated: true, dvirDurationStartTime: true
};
const MOBILE_STATE_FIELDS = new Set(Object.keys(LISTENABLE_MOBILE_FIELDS));

const ADDIN_PRINCIPAL_PREFIX = "addin:";

export class AddinService extends Service {
    private readonly store: DriveStore;
    private readonly deviceCache: BaseCache<IDevice | Device>;
    private readonly trailerCache: BaseCache<ITrailer>;
    private readonly api: Api;
    private readonly hosProcess: HosProcess;
    private readonly sync: Sync;
    private readonly router: Router;
    private readonly translationService: TranslationService;
    private readonly iamSessionIdManagerService: IamSessionIdManager;
    private readonly translatedStrings: { [key: string]: string } = {};
    constructor(
        api: Api,
        hosProcess: HosProcess,
        sync: Sync,
        router: Router,
        store: DriveStore,
        private readonly userService: UserService,
        translationService: TranslationService,
        private readonly dutyStatusLogService: DutyStatusLogService
    ) {
        super();
        this.api = api;
        this.hosProcess = hosProcess;
        this.sync = sync;
        this.router = router;
        this.store = store;
        this.deviceCache = makeDeviceCache(this.store);
        this.trailerCache = makeTrailerCache(this.store);
        this.translationService = translationService;
        const extendSessionId = (sessionId: string) => this.api.callPromise("ExtendSessionId", { sessionId });
        const checkSessionId = (credential) => this.api.callPromise("Get", {
            typeName: "User",
            propertySelector: {
                fields: ["id"],
                isIncluded: true
            },
            search: {
                name: credential.userName
            },
            credentials: credential
        }, true);
        this.iamSessionIdManagerService = new IamSessionIdManager(
            IAM_SESSIONID_REFRESH_INTERVAL_MINUTES,
            () => this.api.callPromise("GetSessionId", {}),
            extendSessionId,
            (session) => this.api.callPromise("Logoff", { credentials: session }),
            (error) => handleApi(error, true),
            this.store,
            checkSessionId
        );
    }

    hasPermission(): CancelablePromise<string> {
        return getNotificationController()
            .hasNotificationPermission()
            .catch(error => {
                logger.error(logger.Source.ADDIN, `Error in hasNotificationPermission() ${ error.message }`);
                return CancelablePromise.resolve(error.message);
            });
    }

    requestPermission(): CancelablePromise<string> {
        return getNotificationController()
            .requestNotificationPermission()
            .catch(error => {
                logger.error(logger.Source.ADDIN, `Error in requestNotificationPermission() ${ error.message }`);
                return CancelablePromise.resolve(error.message);
            });
    }

    notify(message: string, title?: string, id?: number, actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify | null> {
        return getNotificationController()
            .nativeNotify(message, title, id, actions)
            .catch(error => {
                logger.error(logger.Source.ADDIN, `Error in nativeNotify() ${ error.message }`);
                return CancelablePromise.resolve(null);
            });
    }

    cancel(id: number): CancelablePromise<string> {
        return getNotificationController()
            .cancelNativeNotification(id)
            .catch(error => {
                logger.error(logger.Source.ADDIN, `Error in cancelNativeNotification() ${ error.message }`);
                return CancelablePromise.resolve(error.message);
            });
    }

    update(message: string, title?: string, id?: number, actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify | null> {
        return getNotificationController()
            .updateNotification(message, title, id, actions)
            .catch(error => {
                logger.error(logger.Source.ADDIN, `Error in updateNotification() ${ error.message }`);
                return CancelablePromise.resolve(null);
            });
    }

    checkOverlayPermission(): CancelablePromise<boolean> {
        return checkOverlayPermission();
    }

    requestOverlayPermission(): CancelablePromise<boolean> {
        return requestOverlayPermission();
    }

    displayOverlay(html: string, chimes?: string, durationInMilliseconds?: number): CancelablePromise<string> {
        logger.event(logger.Event.ADDINCALL, `Addin is asking to display overlay`);
        return displayOverlay(html, chimes, durationInMilliseconds);
    }

    /**
     * Where the service finds the add-ins the database configures. The database cache is created after this
     * service, so it is handed over once it exists. Until then no add-in is installed as far as the
     * navigation item is concerned, and a call for it resolves false.
     */
    setInstalledAddins(provider: () => readonly IInstalledAddin[]): void {
        this.installedAddins = provider;
    }

    getAddinApi(addinName?: string): IAddinApi {
        return {
            mobile: this.getMobile(addinName),
            authenticate: this.api.authenticate.bind(this.api),
            getSession: (successCallback: TApiCredentialsCallback | null, newSession: boolean, options?: IAddinSessionOptions) => {
                // we get the credentials object from the api with api.getSession
                // this.getSessionId checks if we are an IAM user and if so,
                // the user won't have sessionId in the credentials object
                // we will need to use the iamSessionIdManagerService to fetch an sessionId for the addin
                // if the user is not an IAM user, we will just return the credentials
                return this.api.getSession(null, newSession).then((credentialsWithServer: ICredentialsWithServer) => {
                    return this.getSessionId(credentialsWithServer.credentials, options?.includeAccessToken === true).then(creds => {
                        credentialsWithServer.credentials = creds;
                        successCallback && successCallback(creds, credentialsWithServer.server, credentialsWithServer.iamSettings);
                        return credentialsWithServer;
                    });
                });
            },
            getUser: this.api.getUser.bind(this.api),
            setUser: this.api.setUser.bind(this.api),
            getCurrentCredentials: this.api.getCurrentCredentials.bind(this.api),
            call: this.api.call.bind(this.api),
            multiCall: this.api.multiCall.bind(this.api),
            forget: this.api.forget.bind(this.api)
        };
    }

    destroy() {
        this.iamSessionIdManagerService.destroy();
    }

    releaseAddinEvents(addinName: string): void {
        try {
            window.__rnUnregisterAddinPrincipal?.(`${ ADDIN_PRINCIPAL_PREFIX }${ addinName }`);
        } catch (error) {
            logger.error(logger.Source.ADDIN, `Unable to release lifecycle handlers for ${ addinName }`, this.api.getUser(), error);
        }
    }

    /**
     * Last-user logout: tell the add-ins the drivers are leaving, then drop every lifecycle registration
     * regardless of name. Session.close calls it once the local storage is cleared, just before the user is
     * removed. The announcement comes first because the driver only leaves the active users after the
     * registrations are gone, so the producer's own driverLogout would reach nobody.
     */
    releaseAllAddinEvents(): void {
        announceLoggedInDriversLeaving(this.store);
        try {
            window.__rnClearAddinEvents?.();
        } catch (error) {
            logger.error(logger.Source.ADDIN, "Unable to clear lifecycle handlers", this.api.getUser(), error);
        }
    }

    /**
     * DriveNative ended every session itself (a token that can no longer be refreshed, a forced sign-out, a
     * native logout): no Drive logout ran, so no add-in has been told the drivers left. The shell calls this
     * just before it tears the WebView down.
     *
     * Every driver Drive still lists is leaving with the WebView, so all of them are announced before the
     * registrations are cleared. Calling it again, or after a Drive logout, reaches nobody.
     */
    releaseAddinEventsForNativeSignOut(): void {
        this.releaseAllAddinEvents();
    }

    private installedAddins: () => readonly IInstalledAddin[] = () => [];

    private getMobile(addinName?: string): IAddinApi["mobile"] {
        const principal = addinName ? `${ ADDIN_PRINCIPAL_PREFIX }${ addinName }` : undefined;
        const hasLifecycleChannel = typeof window.__rnRegisterAddinEvent === "function"
            && typeof window.__rnUnregisterAddinEvent === "function"
            && typeof window.__rnGetAddinEvents === "function";
        const lifecycleEvents = principal && hasLifecycleChannel
            ? {
                lifecycleEvents: Object.freeze((window.__rnGetAddinEvents?.() ?? []).slice()) as readonly MobileLifecycleEvent[],
                on: (event: MobileLifecycleEvent, callback: MobileEventCallback) => {
                    const registered = window.__rnRegisterAddinEvent?.(principal, event, callback) === true;
                    if (!registered) {
                        throw new Error(`api.mobile.on: unsupported event or invalid callback (${ event })`);
                    }
                    return () => {
                        window.__rnUnregisterAddinEvent?.(principal, event, callback);
                    };
                },
                off: (event: MobileLifecycleEvent, callback: MobileEventCallback) => {
                    window.__rnUnregisterAddinEvent?.(principal, event, callback);
                }
            }
            : {};
        const bottomBar = addinName && isAddinBottomBarSupported()
            ? {
                bottomBar: {
                    show: () => setAddinBottomBar(addinName, true),
                    hide: () => setAddinBottomBar(addinName, false)
                }
            }
            : {};
        const bottomNavigationItem = addinName && isAddinNavigationItemSupported()
            ? {
                bottomNavigationItem: {
                    set: (item: IBottomNavigationItem) => {
                        let installed: readonly IInstalledAddin[];
                        try {
                            installed = this.installedAddins();
                        } catch (error) {
                            const reason = error instanceof Error ? error.message : "unknown error";
                            logger.error(logger.Source.ADDIN, `bottomNavigationItem could not read the installed add-ins for ${ addinName }: ${ reason }`);
                            return CancelablePromise.resolve(false);
                        }
                        return setAddinNavigationItem(addinName, findUniquelyNamedAddin(installed, addinName)?.url ?? null, item, installed);
                    },
                    clear: () => clearAddinNavigationItem(addinName)
                }
            }
            : {};
        return {
            exists: isNativeApp,
            getVersion: getVersion,
            speak: nativeSpeak,
            notify: (message: string, title?: string, id?: number, actions?: INativeNotifyAction[]) => this.notify(message, title, id, actions),
            geolocation: navigator.geolocation,
            notification: {
                hasPermission: () => this.hasPermission(),
                requestPermission: () => this.requestPermission(),
                notify: (message: string, title?: string, id?: number, actions?: INativeNotifyAction[]) => this.notify(message, title, id, actions),
                cancel: (id: number) => this.cancel(id),
                update: (message: string, title: string, id: number) => this.update(message, title, id)
            },
            overlay: {
                checkPermission: () => this.checkOverlayPermission(),
                requestPermission: () => this.requestOverlayPermission(),
                display: (html: string, chimes?: string, durationInMilliseconds?: number) => this.displayOverlay(html, chimes, durationInMilliseconds)
            },
            ...lifecycleEvents,
            ...bottomBar,
            ...bottomNavigationItem,
            user: {
                get: (includeAllDrivers = true) => {
                    return new CancelablePromise((resolve) => {
                        return resolve(([] as any).concat(includeAllDrivers ? this.userService.getUsers(true) : this.userService.getDriversSeatUser()));
                    });
                },

                setDriverSeat: (id: string) => {
                    return new CancelablePromise(resolve => {
                        let user = this.userService.getUserById(id);
                        if (user) {
                            this.userService.setDriversSeatUser(user.name);
                        }
                        return resolve();
                    });
                },

                getHosRuleSet: () => {
                    return new CancelablePromise<DeepReadonly<IHosRuleset>>((resolve, reject) => {
                        let user = this.api.getUser();
                        if (!user) {
                            reject(undefined);
                            return;
                        }

                        return resolve(this.hosProcess.getDriverRuleSet());
                    });
                },

                getAvailability: () => {
                    let ruleResult = this.hosProcess.getRuleResult(true, true) as IValidation;
                    return new CancelablePromise<IHosAvailability | null>(resolve => resolve(ruleResult ? ruleResult.availability : null));
                },
                getMinAvailabilityHtml: (legacy = true) => {
                    let template = _.template(minAvailabilityHTML);
                    return new CancelablePromise((resolve, reject) => {
                        let ruleResult = this.hosProcess.getRuleResult(true, true) as IValidation;
                        if (!ruleResult  || !ruleResult.availability) {
                            return reject(new Error("No availability"));
                        }
                        let minAvailibility = getMinAvailability(ruleResult.availability);
                        if (minAvailibility.name === "" || minAvailibility.name === undefined) {
                            return reject(new Error("No Minimum availability"));
                        }
                        if (legacy) {
                            const msToString = convertMsToString(minAvailibility.min, TranslatableDisplayStrings.HOUR_UNIT, TranslatableDisplayStrings.MINUTE_UNIT);

                            this.translationService.getTranslator().translate(getAvailabilityName(minAvailibility.name), this.translationService.getLanguage()).then((translatedStr) => {
                                minAvailibility.name = translatedStr;
                                let html = "<p style=\"text-align: center\">";
                                html += "<span style=\"font-size: 40px;\"><b>" + minAvailibility.name + "</b><br>";
                                html += msToString + "</span></p>";
                                return resolve(html);
                            }, () => {
                                // If we get lost in translation
                                return reject(new Error("Error in translation"));
                            });
                        } else {
                            const minimumAvailabilityName = getAvailabilityName(minAvailibility.name);
                            let defaultAvail = this.hosProcess.getDefaultAvailability(this.hosProcess.getLatestHosRuleSetIdShortened(true));
                            let defaultMin = getMinAvailability(defaultAvail).min;
                            if (!defaultMin || defaultMin === Infinity) {
                                defaultMin = 8;
                            }
                            const iconClass = getIconForAvailability(minAvailibility.name);
                            let minimumPercent: any = (minAvailibility.min / defaultMin) * 100;
                            let availabilityPercent = typeof minimumPercent === "number" ? Number(Math.min(minimumPercent, 100).toFixed(1)) : 100;
                            let stringsToTranslate = [TranslatableDisplayStrings.HOUR_UNIT, TranslatableDisplayStrings.MINUTES_STRING, minimumAvailabilityName];
                            this.translate(stringsToTranslate, this.translationService.getLanguage()).then(() => {
                                let data = {};
                                data["iconClass"] = iconClass;
                                data["availabilityPercent"] = availabilityPercent;
                                data["minimumAvailabilityName"] = getTranslatedString(minimumAvailabilityName, this.translatedStrings, this.translationService.getLanguage(), false);
                                let minutesUnitString = getTranslatedString(TranslatableDisplayStrings.MINUTES_STRING, this.translatedStrings, this.translationService.getLanguage(), false)
                                    .replace("{minutes}", "");
                                let hoursUnitString = getTranslatedString(TranslatableDisplayStrings.HOUR_UNIT, this.translatedStrings, this.translationService.getLanguage(), false);
                                data["minimumAvailabilityValue"] = convertMsToString(minAvailibility.min, hoursUnitString, minutesUnitString);
                                let html = template(data);
                                return resolve(html);
                            }, () => {
                                // If we get lost in translation
                                return reject(new Error("Error in translation"));
                            });
                        }

                    });
                },

                getViolations: () => {
                    let ruleResult = this.hosProcess.getRuleResult(true, true) as IValidation;
                    return new CancelablePromise<IViolation[]>(resolve => resolve(ruleResult ? ruleResult.violations : []));
                }
            },

            HOS: {
                getHOSAvailability: () => new CancelablePromise<IHosAvailability>((resolve, reject) => {
                    const ruleResult = this.hosProcess.getRuleResult(true) as IValidation | undefined;
                    if (ruleResult && ruleResult.availability) {
                        resolve(ruleResult.availability);
                    } else {
                        reject(new Error("No availability"));
                    }
                })
            },

            dutyStatusLog: {
                get: () => {
                    return new CancelablePromise<DutyStatusLog[]>(resolve => {
                        let user = this.api.getUser();
                        if (!user) {
                            return resolve([]);
                        }

                        return resolve(this.getStandardStatusLogs(user.id));
                    });
                },

                getForDriver: (driverId: string) => {
                    return new CancelablePromise<DutyStatusLog[]>((resolve, reject) => {
                        // Only a driver signed in to the app (an active user). A known but signed-out driver, or anything
                        // that is not an id, must reject: an empty list would read as "no logs".
                        const driver = this.userService.getUsers(true).find(user => user.id === driverId);
                        if (!driver) {
                            return reject(new Error("api.mobile.dutyStatusLog.getForDriver: the driver is not signed in to the app"));
                        }

                        return resolve(this.getStandardStatusLogs(driver.id));
                    });
                },

                getCurrentDrivingLog: () => {
                    return new CancelablePromise<DutyStatusLog | null>(resolve => {
                        let user = this.api.getUser();
                        if (!user) {
                            return resolve(null);
                        }

                        let log: any = this.hosProcess.getLatestLogForUser(user);
                        return resolve(log ? jsonClone(log) : null);
                    });
                },

                add: (log: DutyStatusLog) => {
                    return new CancelablePromise<DutyStatusLog>(resolve => {
                        return this.sync.dutyStatusLogs.checkValue(log).then(() => {
                            return this.sync.dutyStatusLogs.addItem(log, true);
                        }).then(resolve);
                    }).timeout(SYNC_TIMEOUT);
                }
            },

            vehicle: {
                get: () => {
                    return new CancelablePromise(resolve => {
                        let currVehicle = getCurrentVehicle();
                        if (!currVehicle) {
                            return resolve(null);
                        }

                        let vehicles = this.deviceCache.get(currVehicle);
                        let vehicle = (vehicles.length && vehicles.length > 0) ? vehicles[0] : currVehicle;
                        return resolve(vehicle);
                    });
                },

                set: (id: string) => {
                    return CancelablePromise.reject(new Error(`Not implemented ${ id }`));
                }
            },

            trailer: {
                get: () => {
                    return new CancelablePromise((resolve, reject) => {
                        let user = this.api.getUser();
                        if (!user) {
                            return reject(new Error("No user is selected"));
                        }

                        return resolve(this.trailerCache.get(null, user.id));
                    });
                },

                add: (trailerObj: ITrailer) => {
                    // TODO: Add to local cache. Try API call? Think we need a Trailer sync framework.
                    return CancelablePromise.reject(new Error(`Not implemented ${ trailerObj.id }`));
                }
            },

            trailerAttachment: {
                get: () => {
                    return new CancelablePromise(resolve => {
                        let attachments = new TrailerAttachments(this.api, this.store);
                        attachments.populate();

                        return resolve(attachments.toJSON());
                    });
                },

                add: (trailerId: string) => {
                    return new CancelablePromise((resolve, reject) => {
                        let currentVehicle = getCurrentVehicle();
                        if (!currentVehicle) {
                            return reject(new Error("No current vehicle found"));
                        }

                        let user = this.api.getUser();
                        if (!user) {
                            return reject(new Error("No user is selected"));
                        }

                        let trailer = new Trailer({ id: trailerId });

                        let attachments = new TrailerAttachments(this.api, this.store);

                        if (attachments.isTrailerAttached(trailer)) {
                            return resolve();
                        }

                        let attachment = new TrailerAttachment({
                            id: "",
                            device: { id: currentVehicle.id },
                            trailer: { id: trailer.id },
                            activeFrom: getServerDate().toISOString(),
                            activeTo: constants.MAX_DATE.toISOString()
                        });

                        void this.sync.trailerAttachments.checkValue(attachment).then(() => {
                            return this.sync.trailerAttachments.addItem(attachment);
                        }).then(resolve, reject);
                    });
                },

                remove: (trailerId: string) => {
                    return new CancelablePromise((resolve, reject) => {
                        let attachments = new TrailerAttachments(this.api, this.store);
                        let currentAttachments = attachments.getCurrent();

                        let matchingAttachments = currentAttachments.filter(attachment => attachment.trailer?.id === trailerId);

                        if (!matchingAttachments || matchingAttachments.length === 0) {
                            return reject(new Error("No matching TrailerAttachments"));
                        }

                        matchingAttachments[0].detach();

                        void this.sync.trailerAttachments.setItem(matchingAttachments[0]).then(resolve, reject);
                    });
                }
            },

            shipment: {
                get: () => {
                    return new CancelablePromise(resolve => {
                        let shipmentsFromStore = getShipments(this.store);
                        const shipments: IShipment[] = hashToArray(shipmentsFromStore ? extend(true, {}, shipmentsFromStore) : {});
                        resolve(shipments);
                    });
                },

                add: (shipmentObj: IShipment) => {
                    return new CancelablePromise((resolve, reject) => {
                        let currentVehicle = getCurrentVehicle();
                        let user = this.api.getUser();
                        if (!user) {
                            return reject(new Error("No user is selected"));
                        }
                        if (!currentVehicle) {
                            return reject(new Error("No current vehicle found"));
                        }

                        let shipment = new Shipment(shipmentObj);

                        void this.sync.shipments.checkValue(shipment).then(() => {
                            const deviceId = typeof (shipment.device) === "object" ? shipment.device.id : shipment.device;
                            if (currentVehicle !== null && (!deviceId || deviceId === NODEVICEID)) {
                                shipment.device = currentVehicle;
                            }
                            return this.sync.shipments.addItem(shipment);
                        }).then(resolve, reject);
                    });
                },

                remove: (shipmentId: string) => {
                    return new CancelablePromise((resolve, reject) => {
                        const shipments = getShipments(this.store);
                        if (!shipments[shipmentId]) {
                            return reject(new Error(`No shipment found for given ${ shipmentId }`));
                        }

                        let shipment = new Shipment(shipments[shipmentId]);
                        shipment.detach();

                        void this.sync.shipments.checkValue(shipment).then(() => {
                            return this.sync.shipments.setItem(shipment);
                        }).then(resolve, reject);
                    });
                }
            },

            dvir: {
                get: () => new CancelablePromise<TAddinDvirLog[]>(resolve => resolve(this.getCurrentAssetDvirLogs()))
            },

            textMessage: {
                get: () => {
                    return new CancelablePromise((resolve) => {
                        let user = this.api.getUser();
                        if (!user) {
                            return resolve([]);
                        }
                        let messagesDict: DeepReadonlyObject<IIdDictionary<IMessage>> = this.store.getState().messages[user.id] || {};
                        let messages: IMessage[] = [];
                        if (messagesDict) {
                            messages = Object.keys(messagesDict).map(key => new Message(extend(true, {}, messagesDict[key]) as IMessage));
                        }
                        resolve(messages);
                    });
                },
                add: (message: IMessage) => {
                    return this.sync.messages.checkValue(message)
                        .then(() => this.sync.messages.addItem(message))
                        .timeout(SYNC_TIMEOUT);
                },
                set: (message: IMessage) => {
                    return this.sync.messages.checkValue(message)
                        .then(() => this.sync.messages.setItem(message))
                        .timeout(SYNC_TIMEOUT);
                }
            },

            theme: {
                get: () => new CancelablePromise<IAddinTheme>(resolve => resolve(this.getTheme()))
            },

            driving: {
                get: () => new CancelablePromise<IAddinDriving>(resolve => resolve(this.getDriving()))
            },

            navigate: (url: string, param?: any) => {
                const route = typeof url === "string" ? url.trim() : "";
                // A route that already carries state cannot take a param as well: the two would concatenate into invalid rison
                if (route === "" || (param && route.includes(","))) {
                    throw new Error("Invalid URL");
                }
                this.router.navigateToPage(route, param);
            },

            listenTo: (fieldsOrCallback: MobileStateField | MobileStateField[] | ((state: IMobileStore) => void), callback?: (state: Partial<IMobileStore>) => void) => {
                if (typeof fieldsOrCallback === "function") {
                    return observeStore(this.store, state => state.mobile, fieldsOrCallback as (state: MobileStateSnapshot) => void);
                }

                const requested = typeof fieldsOrCallback === "string" ? [fieldsOrCallback] : fieldsOrCallback;
                if (!Array.isArray(requested) || typeof callback !== "function") {
                    throw new Error("listenTo requires a callback or fields plus a callback");
                }
                // Copy: the selector below runs on every dispatch, so the add-in mutating its own array
                // later must not change what was validated here.
                const fields = [...requested];
                const invalidFields = fields.filter(field => !MOBILE_STATE_FIELDS.has(field));
                if (fields.length === 0 || invalidFields.length > 0) {
                    throw new Error(`listenTo received invalid fields: ${ invalidFields.join(", ") || "empty" }`);
                }

                return observeStore(this.store, state => {
                    const selected = {} as Partial<IMobileStore>;
                    fields.forEach(field => {
                        Object.assign(selected, { [field]: state.mobile[field] });
                    });
                    return selected;
                }, callback as (state: Partial<MobileStateSnapshot>) => void);
            },

            camera: {
                takePicture: (): CancelablePromise => {
                    return new CancelablePromise((resolve) => {
                        let user = this.userService.getCurrentUser();
                        if (!user) {
                            throw new Error("Invalid value for current user");
                        }

                        let pictureDialog = new PictureOptionDialog(this.translationService.getTranslator(), this.store, false);
                        void pictureDialog.open("", (media: IImageResult) => { resolve(media.base64Image); });
                    });
                }
            }
        };
    }

    /**
     * DVIR logs for the current vehicle and its currently attached trailers, read from the
     * signed-in user's bucket of the dvirLogs store.
     *
     * That bucket is filled by DvirSync from a device/trailer search with no user filter, so it
     * already holds other drivers' logs for these assets. That matches what api.call("Get", DVIRLog)
     * returns (DVIR logs are scoped by asset on the server), so nothing new is exposed. Other users'
     * buckets are never read.
     *
     * Synced logs are limited to the last ADDIN_DVIR_LOOKBACK_DAYS days of what Drive holds. Drive
     * keeps the latest pre/post-trip per asset plus anything after it, so this is not a history API.
     * Logs not yet synced are always included and flagged with isPendingSync, since api.call cannot
     * return them.
     */
    private getCurrentAssetDvirLogs(): TAddinDvirLog[] {
        const user = this.api.getUser();
        if (!user) {
            return [];
        }

        const assetIds = new Set<string>();
        const currentVehicle = getCurrentVehicle();
        if (currentVehicle && currentVehicle.id !== NODEVICEID) {
            assetIds.add(currentVehicle.id);
        }
        new TrailerAttachments(this.api, this.store).getCurrentTrailers().forEach(trailer => assetIds.add(trailer.id));
        if (assetIds.size === 0) {
            return [];
        }

        const storedLogs = jsonClone<IIdDictionary<IDVIRLog>>(this.store.getState().dvirLogs[user.id] || {});
        const windowStart = getServerDate().getTime() - ADDIN_DVIR_LOOKBACK_DAYS * DAYS;

        return hashToArray(storedLogs)
            .filter(log => assetIds.has(new DVIRLog(log).getResource().id))
            .map(log => {
                const isPendingSync = isUnsyncedDvirLog(log);
                stripClientOnlyFields(log);
                (log.dVIRDefects || []).forEach(stripClientOnlyFields);
                return { ...log, isPendingSync };
            })
            .filter(log => log.isPendingSync || getDvirTime(log) >= windowStart)
            .sort((a, b) => {
                const aTime = getDvirTime(a);
                const bTime = getDvirTime(b);
                if (isNaN(aTime) || isNaN(bTime)) {
                    // Only a pending log can lack a usable dateTime; keep those after the dated logs.
                    return (isNaN(aTime) ? 1 : 0) - (isNaN(bTime) ? 1 : 0);
                }
                return aTime - bTime;
            });
    }

    /** The theme Drive renders add-ins on; the same rule feeds api.mobile.on("themeChange"). */
    private getTheme(): IAddinTheme {
        return getAddinTheme(this.store);
    }

    /** A copy of a driver's standard-status duty logs, so the add-in never holds what the app holds. */
    private getStandardStatusLogs(userId: string): DutyStatusLog[] {
        return jsonClone(this.dutyStatusLogService.getHOSLogs(true, userId).filter(log => log.isStandardStatusDriverLog()));
    }

    /**
     * The driving state the app currently holds (readDriving: state.data.driving, or the native movement
     * service's state once the host owns the lock screen), the same value that drives the lock screen
     * the driver sees and api.mobile.on("drivingChange"). It gives an add-in that starts while the
     * vehicle is already moving the starting state the event never announces. While the live value is
     * unknown it returns the last true/false the current owner held (the web app, or native once the host
     * owns the lock screen), because the event would not announce that again. The other owner's value is
     * never a fallback. null when the current owner has not known yet. The public contract is true, false or null.
     */
    private getDriving(): IAddinDriving {
        const driving = readDriving(this.store);
        return { isDriving: typeof driving === "boolean" ? driving : (getLastKnownDriving(this.store) ?? null) };
    }

    /** The signed-in user's native token, or undefined on hosts without the native token module or when it has none. */
    private getAppAuthToken(userName: string): Promise<{ accessToken?: string } | undefined> {
        if (!isGetTokenModule()) {
            return Promise.resolve(undefined);
        }

        return new Promise(resolve => {
            getToken(userName, (_err, token) => {
                resolve(token || undefined);
            });
        });
    }

    private async getSessionId(credentials: ICredentials, includeAccessToken = false) {
        const appAuthToken = credentials.iamJwt ? undefined : await this.getAppAuthToken(credentials.userName);
        if (credentials.iamJwt || appAuthToken) {
            // if iamSessionIdManagerService.getSessionId fetch and subsequence extension calls fails
            // we will follow mygeotab example and retries the call within a minute
            return this.iamSessionIdManagerService.getSessionId(credentials).then(async (sessionId) => {
                credentials.sessionId = sessionId;
                if (!includeAccessToken || !appAuthToken) {
                    return credentials;
                }

                // Only on request: add-ins that read iamJwt choose their sign-in from it (a popup sign-in has one, an AppAuth
                // sign-in did not), so adding it unasked would switch them from the sessionId to the token (MYG-156100).
                // Asked for after the session call, so the token is as fresh as the native layer can make it.
                const freshToken = await this.getAppAuthToken(credentials.userName);
                // A copy: the api's live credentials (newSession) must not gain an iamJwt, or its calls would stop sending credentials.
                return freshToken?.accessToken ? { ...credentials, iamJwt: freshToken.accessToken } : credentials;
            });
        }
        return Promise.resolve(credentials);
    }

    private translate(stringsToTranslate: string[], language?: string): Promise<void> {
        return this.translationService.getTranslator().translate(stringsToTranslate, language).then((translatedStr) => {
            stringsToTranslate.forEach((str, index) => {
                this.translatedStrings[str] = translatedStr[index];
            });
        });
    }
}

export interface IAddinSessionOptions {
    /**
     * Also return the signed-in user's Keycloak access token as `credentials.iamJwt`, the field a web-popup sign-in already
     * carries. Needed on Drive 7, where the sign-in is native and the credentials have no iamJwt. Absent or false: credentials
     * are returned as before. Read the token again on each call, it is not refreshed for you.
     */
    includeAccessToken?: boolean;
}

export interface IAddinApi {
    mobile: {
        exists: () => boolean;
        getVersion: () => string | null;
        speak: (phrase: string, rate: number | null | undefined, lang: string | null | undefined) => void;
        notify: (message: string, title?: string | undefined, id?: number | undefined, actions?: INativeNotifyAction[] | undefined) => CancelablePromise<INativeNotify | null>;
        notification: {
            hasPermission: () => CancelablePromise<string>;
            requestPermission: () => CancelablePromise<string>;
            notify: (message: string, title?: string | undefined, id?: number | undefined, actions?: INativeNotifyAction[] | undefined) => CancelablePromise<INativeNotify | null>;
            cancel: (id: number) => CancelablePromise<string>;
            update: (message: string, title: string, id: number) => CancelablePromise<INativeNotify | null>;
        };
        overlay: {
            checkPermission: () => CancelablePromise<boolean>;
            requestPermission: () => CancelablePromise<boolean>;
            display: (html: string, chimes?: string, durationInMilliseconds?: number) => CancelablePromise<string>;
        };
        /**
         * Show or hide the native bottom navigation bar while this add-in's page is open (MYG-154380). Hidden by
         * default. Present only for a named add-in on a DriveNative build that has it; check before using.
         * Resolves true when the request was taken and false when it was not (not this add-in's page, native
         * navigation off, no answer). The request ends when the driver leaves the page.
         */
        bottomBar?: {
            show: () => CancelablePromise<boolean>;
            hide: () => CancelablePromise<boolean>;
        };
        /**
         * Put one item in the native bottom navigation bar, in the place of one of its buttons: Messages unless
         * `replaces` names another (MYG-154446). Only a short list of add-ins may; for any other add-in every call
         * resolves false. Present only for a named add-in on a DriveNative build that has it; check before using.
         *
         * `set` places or replaces the item; `clear` removes it and the button it replaced comes back. Both
         * resolve true when the request was taken and false when it was not. `set` resolves false when the add-in
         * is not on the list, its name is shared with another installed add-in, the item is not acceptable, no
         * add-in of the product named in `action` is installed (or its name is shared), another add-in owns the
         * item, native navigation is off, the driver has no such button, native sent an error, or native did not
         * answer. `clear` resolves false when the add-in has no item or native failed. Neither rejects. `clear`
         * works for the add-in that owns the item even if it has left the list since. A `false` from `set`
         * because native did not answer does not prove that no item was placed: call `clear` to be sure.
         *
         * The first add-in to place an item owns it until it clears it, the driver logs out or the viewer
         * changes. Use `releaseOnPress` to hand the item to the add-in a tap opens.
         */
        bottomNavigationItem?: {
            set: (item: IBottomNavigationItem) => CancelablePromise<boolean>;
            clear: () => CancelablePromise<boolean>;
        };
        /** Events this host can deliver; present only when on/off are. Check before subscribing. */
        lifecycleEvents?: readonly MobileLifecycleEvent[];
        on?: (event: MobileLifecycleEvent, callback: MobileEventCallback) => Unsubscribe;
        off?: (event: MobileLifecycleEvent, callback: MobileEventCallback) => void;
        geolocation: Geolocation;
        user: {
            get: (includeAllDrivers?: boolean) => CancelablePromise;
            setDriverSeat: (_: string) => CancelablePromise;
            getHosRuleSet: () => CancelablePromise;
            getAvailability: () => CancelablePromise;
            getMinAvailabilityHtml: (legacy?: boolean) => CancelablePromise;
            getViolations: () => CancelablePromise;
        };
        /**
         * Same availability as `user.getAvailability()`, read from the latest processed result (no forced reprocess).
         * Rejects when there is no result, where `user.getAvailability()` resolves null. The Attrix mobileShim
         * (MYG-148981) probes for this member before falling back to the private `driveApp.hosUtils` read.
         */
        // eslint-disable-next-line @typescript-eslint/naming-convention -- the name add-ins probe for is fixed by the Attrix mobileShim
        HOS: {
            getHOSAvailability: () => CancelablePromise<IHosAvailability>;
        };
        dutyStatusLog: {
            get: () => CancelablePromise;
            getCurrentDrivingLog: () => CancelablePromise;
            /**
             * Duty status logs of another driver who is signed in to the app (a co-driver). Rejects for a driver who is
             * not signed in. Absent on builds before MYG-148942: check typeof before calling, because get() only ever
             * returns the signed-in driver's logs.
             */
            getForDriver: (driverId: string) => CancelablePromise;
            add: (_: DutyStatusLog) => CancelablePromise;
        };
        vehicle: {
            get: () => CancelablePromise;
            set: (id: string) => CancelablePromise;
        };
        trailer: {
            get: () => CancelablePromise;
            add: (_: ITrailer) => CancelablePromise;
        };
        trailerAttachment: {
            get: () => CancelablePromise;
            add: (_: string) => CancelablePromise;
            remove: (_: string) => CancelablePromise;
        };
        shipment: {
            get: () => CancelablePromise;
            add: (shipment: IShipment) => CancelablePromise;
            remove: (id: string) => CancelablePromise;
        };
        /**
         * Read-only DVIR access (MYG-148948). Add-ins must feature-detect `api.mobile.dvir` before calling it.
         * get() resolves to DVIR logs for the current vehicle and its attached trailers, from any driver,
         * sorted oldest first: synced logs from the last ADDIN_DVIR_LOOKBACK_DAYS days of what Drive holds,
         * plus every log not yet synced (isPendingSync: true). Resolves to [] when no user or asset is selected.
         */
        dvir: {
            get: () => CancelablePromise<TAddinDvirLog[]>;
        };
        /**
         * Read-only theme state (MYG-148949). Add-ins must feature-detect `api.mobile.theme` before calling it.
         * get() resolves to the theme Drive currently renders add-ins on.
         */
        theme: {
            get: () => CancelablePromise<IAddinTheme>;
        };
        /**
         * Read-only driving state (MYG-148950). Add-ins must feature-detect `api.mobile.driving` before calling it.
         * get() resolves to { isDriving }: true or false as the app last knew it, or null while the source that owns the driving state has not known it yet.
         * Use it for the starting state; api.mobile.on("drivingChange") announces changes after that.
         */
        driving: {
            get: () => CancelablePromise<IAddinDriving>;
        };
        /**
         * Opens a Drive route: a page name from `router.getAliases()`, or any other route such as another add-in's
         * `addin,(addinName:<base64>)` (pass the state in `param` or in the route, not both).
         * An add-in page opens only for an add-in the driver can see. Pages covered by `PAGE_SECURITY_DEFS` require the
         * driver's clearances, other pages open without a check, and an unknown page falls back to the dashboard.
         * Throws "Invalid URL" for an empty or non-string route, or a route with state plus a `param`; errors from the
         * router or from encoding `param` (for example a circular object) are rethrown. Add-ins should only call it
         * after a driver action.
         */
        navigate: (url: string, param?: any) => void;
        listenTo: {
            (callback: (state: IMobileStore) => void): Unsubscribe;
            (fields: MobileStateField | MobileStateField[], callback: (state: Partial<IMobileStore>) => void): Unsubscribe;
        };
        textMessage: {
            get: () => CancelablePromise;
            add: (message: IMessage) => CancelablePromise;
            set: (message: IMessage) => CancelablePromise;
        };
        camera: {
            takePicture: () => CancelablePromise;
        };
    };
    authenticate: (newServer: string, database: string, username: string, password: string, success: TApiSuccessCallback, error: TApiErrorCallback) => IApiCall;
    getSession: (successCallback: TApiCredentialsCallback | null, newSession: boolean, options?: IAddinSessionOptions) => CancelablePromise<ICredentialsWithServer>;
    getUser: () => IUser | null;
    setUser: (credentials: ICredentials, server: string) => void;
    getCurrentCredentials: () => ICredentialsWithServer | null;
    call: (method: string, params: any, callbackSuccess?: TApiSuccessCallback, callbackError?: TApiErrorCallback, ignoreCredentials?: boolean) => void;
    multiCall: (calls: TMultiCallParam[], callbackSuccess: TApiSuccessCallback, callbackError: TApiErrorCallback) => void;
    forget: (successCallback?: TApiSuccessCallback, errorMessage?: string) => void;
}
